import { queryOptions } from "@tanstack/react-query";
import type { TenantProductLine, TenantType } from "@nexus/contracts";
import { authFetch, ApiError } from "@/lib/api-client";
import {
  API_REQUEST_TIMEOUT_LONG_MS,
  PAGE_STATE_GC_TIME,
  RETRY_DELAY_MS,
} from "@/lib/query-constants";

// ─── Types ──────────────────────────────────────────────────────────

export interface TenantPermission {
  type: string;
  method: string;
  route: string;
}

export interface TenantPermissions {
  id: string;
  name: string;
  type: TenantType;
  permissions: TenantPermission[];
  /** Name of the user's assigned role (Access group) in this tenant, e.g. "Admins" or a custom "Adge Customer". Absent on legacy responses. */
  roleName?: string | null;
  /** Tenant logo (signed URL resolved by the backend). Absent when no logo is set. */
  logoUrl?: string | null;
  /** Explore product line. Absent on sessions cached before it shipped — treat as CORE_AND_COMP. */
  productLine?: TenantProductLine;
}

/** Removes SUPER_TENANT entries from a tenant-shaped array. */
export function filterSelectableTenants<T extends { type: TenantType }>(tenants: T[]): T[] {
  return tenants.filter((t) => t.type !== "SUPER_TENANT");
}

export interface TenantPages {
  id: string;
  name: string;
  pages: string[];
}

export function toTenantPages(tenants: TenantPermissions[]): TenantPages[] {
  return tenants.map((t) => ({
    id: t.id,
    name: t.name,
    pages: [
      ...new Set(
        t.permissions.filter((p) => p.type === "PAGE").map((p) => p.route),
      ),
    ],
  }));
}

export interface PageStateResponse {
  tenants: TenantPages[];
  pageState: Record<string, unknown>;
}

// ─── PageAccessError ────────────────────────────────────────────────

export class PageAccessError extends Error {
  override readonly name = "PageAccessError";
  constructor(public readonly pageSlug: string) {
    super(`Access denied to page: ${pageSlug}`);
  }
}

// ─── Query Key Factory ──────────────────────────────────────────────

export const pageStateKeys = {
  all: ["page-state"] as const,
  detail: (slug: string) => ["page-state", slug] as const,
} as const;

async function fetchPageState(pageSlug: string): Promise<PageStateResponse> {
  try {
    const { data } = await authFetch<PageStateResponse>(
      `/pages/${encodeURIComponent(pageSlug)}/state`,
      // Permission resolution + tenant pages query on a cold dev pod can
      // legitimately exceed the default 15s; opt into the long timeout.
      { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
    );
    return data;
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) {
      throw new PageAccessError(pageSlug);
    }
    throw error;
  }
}

/**
 * Page-state query with `staleTime: 0` (always refetch on mount),
 * custom retry (no retry on 403), and `skipGlobalError` (component handles display).
 */
export function pageStateQueryOptions(slug: string) {
  return queryOptions({
    queryKey: pageStateKeys.detail(slug),
    queryFn: () => fetchPageState(slug),
    staleTime: 0,
    gcTime: PAGE_STATE_GC_TIME,
    retry: (failureCount, error) => {
      if (error instanceof PageAccessError) return false;
      if (error instanceof ApiError && error.status === 403) return false;
      return failureCount < 2;
    },
    retryDelay: RETRY_DELAY_MS,
    meta: { skipGlobalError: true },
  });
}
